import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import { customUrlEncode, prepareFilters } from '@/utils/apiParamsUtils';

/**
 * Retrieves all guidelines.
 * @returns {Promise<Object[]>} A promise that resolves with an array of guideline objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllGuidelines = async (params) => {
  const filters = prepareFilters(params, {
    category: 'default',
  });

  try {
    const response = await axiosClient.get('/guidelines', {
      params: {
        filter: filters,
        sorter: {},
        // The screen renders every guideline of a category at once — two
        // columns of cards, no pager anywhere. Without a limit the server
        // applies its default of 10, so the eleventh guideline filed under a
        // category simply stopped appearing, with nothing on the page to hint
        // that more existed. The same 1.000 the category calls on this screen
        // already send, so both halves of it ask for a category the same way.
        limit: params?.limit ?? 1000,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve guidelines categories:', error);
    throw error;
  }
};

/**
 * Deletes a guideline by its ID.
 * @param {string} guidelineId - The unique identifier of the guideline to delete.
 * @returns {Promise<Object>} A promise that resolves with the response data after successful deletion.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteGuidelineById = async (guidelineId) => {
  try {
    const response = await axiosClient.delete(`/guidelines/${guidelineId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete guideline with ID ${guidelineId}:`, error);
    throw error;
  }
};

/**
 * Creates a new guideline using multipart/form-data.
 * @param {FormData} formData - The form data containing guideline information and optional file.
 * @returns {Promise<Object>} A promise that resolves to the created guideline object.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewGuideline = async (formData) => {
  try {
    const response = await axiosClient.post('/guidelines/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create guideline:', error);
    throw error;
  }
};

/**
 * Updates an existing guideline by its ID using multipart/form-data.
 * @param {string} id - The unique identifier of the guideline to update.
 * @param {FormData} formData - The updated guideline data, possibly including a new file.
 * @returns {Promise<Object>} A promise that resolves to the updated guideline object.
 * @throws Will throw an error if the update request fails.
 */

export const updateGuidelineById = async (id, formData) => {
  try {
    const response = await axiosClient.put(`/guidelines/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update the guideline with ID: ${id}`, error);
    throw error;
  }
};

const GuidelinesService = {
  retrieveAllGuidelines,
  deleteGuidelineById,
  createNewGuideline,
  updateGuidelineById,
};

export default GuidelinesService;
